{
  "resume_prompt": {
    "title": "Recording paused",
    "reason_call": "Your meeting was paused by an incoming call.",
    "reason_headset": "Your headset got disconnected — we paused the recording to avoid capturing unexpected audio.",
    "reason_other": "Your meeting was paused by a system interruption.",
    "discard": "Discard",
    "resume": "Resume"
  },
  "recorder": {
    "default_title": "Meeting on {date}",
    "title": "New recording",
    "subtitle": "Record your meeting and AI will extract actionable tasks.",
    "field_title": "Title",
    "destination": "Destination",
    "personal": "Personal",
    "choose_project": "Choose project…",
    "attendees": "Attendees ({selected}/{total})",
    "select_all": "All",
    "only_me": "Only me",
    "monthly_quota": "Monthly quota",
    "max_per_meeting": "Up to {n} min per meeting",
    "max_per_meeting_unlimited": "No per-meeting limit",
    "save_audio": "Save audio",
    "start_recording": "Start recording",
    "of_max": "of max {time}",
    "silence_warning": "Are you speaking? We're not capturing any audio.",
    "pause": "Pause",
    "resume": "Resume",
    "stop_process": "Stop and process",
    "cancel_discard": "Cancel and discard",
    "uploading": "Uploading audio…",
    "oem_warning": "On your {device} we recommend keeping Tworks open during the recording. Locking the screen may interrupt it. Continue anyway?",
    "unsupported_browser": "Your browser doesn't support audio recording.",
    "mic_denied_mobile": "Couldn't access the microphone. Allow access from Settings → Apps → Tworks → Permissions.",
    "mic_denied_web": "Couldn't access the microphone. Grant permission from the browser.",
    "no_audio": "We're not receiving audio. The recording may have failed.",
    "paused_long": "You've been paused for more than 5 minutes. Are you sure you want to continue the recording?",
    "save_failed": "Couldn't save the recording. Please retry.",
    "mode_record": "Record",
    "mode_upload": "Upload file",
    "drop_audio_here": "Drag your audio file here",
    "drop_audio_hint": "or click to choose · .mp3, .m4a, .wav, .ogg, .webm, .mp4, .flac (max 200 MB)",
    "audio_click_change": "Click to change",
    "audio_duration": "Duration: {time}",
    "audio_bad_format": "Unsupported format. Accepts {formats}.",
    "audio_too_big": "File too large (max 200 MB).",
    "audio_no_duration": "Couldn't read the audio duration. Try another format (mp3, m4a or wav).",
    "audio_too_long": "This audio is {real} min long and your plan allows up to {max} min per meeting.",
    "audio_exceeds_quota": "This audio is {real} min long but you only have {left} min left this month.",
    "select_audio_first": "Select an audio file first.",
    "select_attendee_required": "Select at least one attendee.",
    "process_audio": "Process audio",
    "ai_output_label": "AI output",
    "ai_mode_full": "Summary + tasks",
    "ai_mode_summary": "Summary only",
    "ai_instructions_toggle": "Instructions for the AI (optional)",
    "ai_instructions_placeholder": "E.g.: focus on the budget agreements and keep it brief"
  },
  "list": {
    "title": "Meetings",
    "subtitle": "Record meetings and let AI extract actionable tasks.",
    "new_recording": "New recording",
    "stat_total": "Total",
    "stat_pending": "Pending",
    "stat_committed": "Committed",
    "stat_quota": "Quota",
    "loading": "Loading…",
    "empty_title": "You haven't recorded any meetings yet",
    "empty_subtitle": "AI turns your recordings into actionable tasks, assignees, and an executive summary — automatically.",
    "empty_cta": "Record your first meeting",
    "tasks_count": "{n} tasks",
    "review": "Review →",
    "view": "View",
    "retry": "Retry",
    "delete": "Delete",
    "personal": "Personal",
    "confirm_delete_title": "Delete this meeting?",
    "confirm_delete_message": "\"{title}\" will be removed along with its audio, transcript, and proposed tasks. Tasks already committed won't be deleted.",
    "confirm_delete_action": "Delete",
    "status": {
      "processing": "Processing",
      "ready": "Pending review",
      "committed": "Tasks created",
      "failed": "Failed",
      "discarded": "Discarded"
    },
    "groups": {
      "today": "Today",
      "yesterday": "Yesterday",
      "this_week": "This week",
      "this_month": "This month",
      "earlier": "Earlier"
    },
    "relative": {
      "just_now": "just now",
      "minutes": "{n}m ago",
      "hours": "{n}h ago",
      "days": "{n}d ago"
    },
    "duration_dash": "—"
  },
  "transcripts": {
    "title": "Transcripts",
    "subtitle": "Import transcripts from Meet, Teams, Otter, or Fireflies. AI extracts actionable tasks.",
    "upload": "Upload transcript",
    "stat_total": "Total",
    "stat_pending": "Pending",
    "stat_committed": "Committed",
    "stat_this_month": "This month",
    "loading": "Loading…",
    "empty_title": "You haven't uploaded any transcripts yet",
    "empty_subtitle": "Accepts .txt, .pdf or .docx files. AI detects tasks, assignees, and due dates automatically.",
    "empty_cta": "Upload my first transcript",
    "review": "Review",
    "view": "View",
    "retry": "Retry",
    "delete": "Delete",
    "personal": "Personal",
    "chars_short": "{n} chars",
    "chars_k": "{n}k chars",
    "confirm_delete_title": "Delete this transcript?",
    "confirm_delete_message": "\"{title}\" and its proposed tasks will be removed. Tasks already created (committed) won't be deleted.",
    "confirm_delete_action": "Delete"
  },
  "review": {
    "back": "Back",
    "back_to_meetings": "Back to meetings",
    "loading": "Loading meeting…",
    "processing_title": "Analyzing your meeting…",
    "processing_subtitle_html": "AI is reading the transcript and extracting actionable tasks. This takes between <strong>{from} and {to} seconds</strong>. No need to reload — this page updates itself.",
    "processing_range_from": "5",
    "processing_range_to": "30",
    "failed_title": "Couldn't process",
    "retry": "Retry",
    "loading_audio": "Loading audio…",
    "audio_load_failed": "Couldn't load the audio.",
    "transcript_full": "Full transcript",
    "tasks_proposed": "Proposed tasks ({selected}/{total})",
    "tasks_created": "Created tasks ({n})",
    "add_manual": "Add manual",
    "no_tasks_html": "No actionable tasks were detected.<br>You can add one manually.",
    "summary_title": "Executive summary",
    "summary_save": "Save",
    "summary_edit": "Edit",
    "summary_empty": "No summary. Click \"Edit\" to add notes.",
    "summary_placeholder": "## Topics covered\n- ...",
    "discard_all": "Discard all",
    "tasks_selected_one": "{selected} of {total} task selected",
    "tasks_selected_other": "{selected} of {total} tasks selected",
    "creating": "Creating…",
    "create_n_tasks_one": "Create {n} task",
    "create_n_tasks_other": "Create {n} tasks",
    "committed_badge": "Tasks created",
    "committed_badge_archived": " · archived",
    "pdf_generating_title": "Generating PDF…",
    "pdf_download_title": "Download summary as PDF",
    "pdf_generating": "Generating…",
    "pdf_short": "PDF",
    "confirm_discard_title": "Discard the meeting?",
    "confirm_discard_message": "The meeting will be removed along with any proposed tasks you haven't committed. This action can't be undone.",
    "confirm_discard_action": "Discard",
    "select_at_least_one": "Select at least one task with a title.",
    "pdf_failed": "Couldn't generate the PDF. Try again.",
    "refresh_failed_console": "Couldn't refresh the meeting",
    "retry_failed_console": "Couldn't retry",
    "audio_failed_console": "Couldn't load the audio",
    "save_summary_only": "Save minutes",
    "summary_only_badge": "Summary only"
  }
}
